import {
  Calendar,
  Package,
  TrendDown,
  Warning,
  ShieldCheck,
  Info,
  ShoppingCart,
} from "@phosphor-icons/react"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import { formatNumber } from "~/utils/formatting"
import { DateFormatType, formatDate, differenceInWeeksFloat } from "~/utils/datetime"
import { weeklyDemandDuringCover } from "~/utils/weeksOnHand"
import { cn } from "~/utils/helpers"

interface Props {
  weeksOnHand: number
  expectedArrivalDate: Date
  endOfPeriodDate: Date
  stockOutDate?: Date
  projectedStockAtArrival: number | undefined
  /** Forecast demand for the purchase period. */
  forecastDemand: number
  /**
   * Forecast units for `weeks` weeks from the arrival date, on the same warehouses the runway is
   * simulated against. Called only while the card is open — the covered window is read off the
   * forecast curve, which is too expensive to precompute for every row on every quantity edit.
   */
  demandForWeeks: (weeks: number) => number
  orderQuantity: number
  safetyStock?: number
  isAggregated?: boolean
  stockoutBeforeArrival?: boolean
}

export function ProjectedWeeksOnHandField(props: Props) {
  const { weeksOnHand, stockoutBeforeArrival } = props
  return (
    <HoverCard>
      <HoverCardTrigger className="flex h-full w-full items-center gap-1">
        {stockoutBeforeArrival && <Warning className="text-warning shrink-0" />}
        {isFinite(weeksOnHand) ? weeksOnHand.toFixed(1) : "52+"}
      </HoverCardTrigger>
      <HoverCardContent className="w-80 p-0" align="start">
        <ProjectedWeeksOnHandFieldContent {...props} />
      </HoverCardContent>
    </HoverCard>
  )
}

export function ProjectedWeeksOnHandFieldContent({
  weeksOnHand,
  expectedArrivalDate,
  endOfPeriodDate,
  stockOutDate,
  projectedStockAtArrival,
  forecastDemand,
  demandForWeeks,
  orderQuantity,
  safetyStock,
  isAggregated,
  stockoutBeforeArrival,
}: Props) {
  const weeksInPeriod = Math.max(differenceInWeeksFloat(expectedArrivalDate, endOfPeriodDate), 1)
  const weeklyDemand = weeklyDemandDuringCover({ weeksOnHand, weeksInPeriod, demandForWeeks })

  return (
    <>
      <div className="px-4 py-3 border-b border-base bg-base">
        <div className="flex items-center justify-between">
          <span className="label-base text-fg-base">Projected weeks on hand</span>
        </div>
        <div className="text-2xl font-semibold mt-1 text-fg-base">
          {isFinite(weeksOnHand) ? `${weeksOnHand.toFixed(1)} weeks` : "52+ weeks"}
          {isAggregated && <span className="paragraph-sm text-fg-subtle ml-1">(avg)</span>}
        </div>
        <p className="paragraph-sm text-fg-subtle mt-1">Runway after this order arrives</p>
      </div>

      <div className="px-4 py-3 space-y-3 bg-base">
        <div className="space-y-2">
          <Row
            icon={<Calendar />}
            label="Arrives"
            value={formatDate(expectedArrivalDate, DateFormatType.FullDate)}
          />
          {stockOutDate && (
            <Row
              icon={<Warning />}
              label="Stock-out"
              value={formatDate(stockOutDate, DateFormatType.FullDate)}
            />
          )}
        </div>

        <hr className="border-t border-dashed border-base" />

        <div className="space-y-2">
          <Row
            icon={<ShoppingCart />}
            label="This order"
            value={`${formatNumber(orderQuantity)} units`}
          />
          <Row
            icon={<Package />}
            label="Stock at arrival"
            value={
              projectedStockAtArrival != null
                ? `${formatNumber(projectedStockAtArrival)} units`
                : "—"
            }
          />
          <Row
            icon={<TrendDown />}
            label="Weekly demand"
            value={`${formatNumber(weeklyDemand, weeklyDemand < 10 ? 1 : 0)} units/wk`}
          />
          <Row
            icon={<Calendar />}
            label="Demand in period"
            value={`${formatNumber(forecastDemand, forecastDemand < 10 ? 1 : 0)} units / ${formatNumber(weeksInPeriod, 1)} wks`}
          />
          {safetyStock != null && safetyStock > 0 && (
            <Row
              icon={<ShieldCheck />}
              label="Safety stock"
              value={`${formatNumber(safetyStock)} units`}
            />
          )}
        </div>
      </div>

      <div
        className={cn(
          "px-4 py-3 border-t border-base bg-subtle flex items-start gap-2 text-fg-subtle",
          {
            "bg-warning/33": stockoutBeforeArrival,
          },
        )}
      >
        <Info className="shrink-0" />
        <p className="paragraph-sm">
          {stockoutBeforeArrival
            ? isAggregated
              ? // A variant's SKUs each have their own run-out; one of them running dry early does
                // not say the whole variant does.
                "Some SKUs run out of stock before this order arrives"
              : "Stock-out expected before this order arrives"
            : "Time from arrival to stock-out"}
        </p>
      </div>
    </>
  )
}

function Row({
  icon,
  label,
  value,
  className,
}: {
  icon: React.ReactNode
  label: string
  value: string
  className?: string
}) {
  return (
    <div className="flex items-center justify-between paragraph-sm">
      <span className="flex items-center gap-2 text-fg-subtle">
        {icon}
        {label}
      </span>
      <span className={cn("font-medium text-fg-base", className)}>{value}</span>
    </div>
  )
}
